import type { ReactNode } from "react";
import { ArrowRight, Check, Compass, Desktop, Eye, Pause } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

/**
 * One small, focused mock per "How" step: a single component, not a screen.
 * Mostly sans type at a readable size, one violet accent each, no query
 * languages or log rows. Static (no timers or motion), readable at 375px.
 * Every value is example data and each card says so.
 */

function MockCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "relative flex flex-col gap-3 rounded-2xl border border-rule bg-card p-4 shadow-[0_1px_0_rgba(20,17,43,0.04)] sm:p-5",
        className
      )}
    >
      {children}
      <span className="absolute right-3 top-3 rounded-md border border-rule px-1.5 py-0.5 text-[10px] font-medium text-ink-subtle">
        Example
      </span>
    </div>
  );
}

function ScoutAvatar() {
  return (
    <span
      className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary-200 text-primary-600"
      aria-hidden
    >
      <Compass size={15} weight="bold" />
    </span>
  );
}

/* 01 Ask: a question, and Scout's plan with the tools it will search. */
export function AskStepMock() {
  const plan = [
    { tool: "Splunk", lang: "SPL" },
    { tool: "Microsoft Sentinel", lang: "KQL" },
    { tool: "Your lake", lang: "SQL" },
  ];
  return (
    <MockCard>
      <p className="mr-16 self-start rounded-xl rounded-tl-sm bg-mono-100 px-3 py-2 text-sm text-ink">
        Were we hit by the sign-in campaign in today&apos;s advisory?
      </p>
      <div className="flex items-start gap-2.5">
        <ScoutAvatar />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <p className="text-sm text-ink">
            I&apos;ll check new-country sign-ins for the last 7 days in three places:
          </p>
          <ul className="flex flex-col gap-1.5">
            {plan.map((p) => (
              <li
                key={p.tool}
                className="flex items-center justify-between gap-3 rounded-lg border border-rule px-3 py-1.5 text-sm"
              >
                <span className="truncate text-ink">{p.tool}</span>
                <span className="shrink-0 text-xs text-ink-subtle">query in {p.lang}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </MockCard>
  );
}

/* 02 Search the tools you already run: one hunt fanned out, one timeline back. */
export function SearchStepMock() {
  const sources = [
    { name: "Splunk", rows: 12 },
    { name: "Microsoft Sentinel", rows: 3 },
    { name: "CrowdStrike Falcon", rows: 1 },
    { name: "Your lake", rows: 0 },
  ];
  const total = sources.reduce((n, s) => n + s.rows, 0);
  return (
    <MockCard>
      <p className="text-sm font-semibold text-ink">One hunt, four sources</p>
      <ul className="flex flex-col gap-1.5">
        {sources.map((s) => (
          <li key={s.name} className="flex items-center gap-3 text-sm">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-ink">{s.name}</span>
            <span className={s.rows ? "text-ink" : "text-ink-subtle"}>
              {s.rows} {s.rows === 1 ? "row" : "rows"}
            </span>
          </li>
        ))}
      </ul>
      <p className="flex items-center gap-2 rounded-lg bg-primary-200/60 px-3 py-2 text-sm text-primary-900">
        <ArrowRight size={14} weight="bold" aria-hidden />
        {total} rows on one timeline, each labelled by source. Nothing copied first.
      </p>
    </MockCard>
  );
}

/* 03 Ask the host: a live answer no log held, and an action waiting on a second approver (four-eyes: someone other than the requester). */
export function HostStepMock() {
  return (
    <MockCard>
      <p className="flex items-center gap-2 text-sm font-semibold text-ink">
        <Desktop size={16} weight="bold" aria-hidden />
        WKS-0412
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-signal-ink">
          <span className="signal-dot" aria-hidden />
          live
        </span>
      </p>
      <p className="text-sm text-ink-muted">Which programs loaded a DLL from a public folder?</p>
      <p className="rounded-lg border border-rule px-3 py-2 text-sm text-ink">
        rundll32.exe loaded <b className="font-semibold">cache.dll</b>, unsigned.
        <span className="mt-0.5 block text-xs text-ink-subtle">
          Not in any log. Answered by the host.
        </span>
      </p>
      <div className="flex items-center justify-between gap-3 rounded-lg border border-primary-300 px-3 py-2 text-sm">
        <span className="text-ink">Isolate WKS-0412</span>
        <span className="shrink-0 text-xs font-medium text-primary-600">Waiting for a second approver</span>
      </div>
    </MockCard>
  );
}

/* 04 Keep watching: the hunt becomes a watcher, staged before it goes live. */
export function WatchStepMock() {
  const stages = [
    { label: "Backtested", done: true },
    { label: "Shadow", done: true },
    { label: "On", done: false },
  ];
  return (
    <MockCard>
      <p className="mr-16 flex items-center gap-2 text-sm font-semibold text-ink">
        <Eye size={16} weight="bold" aria-hidden />
        Watcher
      </p>
      <p className="text-sm text-ink">
        New-country sign-in, then an unsigned DLL on the same user&apos;s host
      </p>
      <ol className="flex items-center gap-1.5" aria-label="Rollout">
        {stages.map((s, i) => (
          <li key={s.label} className="flex items-center gap-1.5">
            {i > 0 && <span className="h-px w-3 bg-rule sm:w-5" aria-hidden />}
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
                s.done ? "bg-mono-100 text-ink" : "bg-primary-500 text-white"
              )}
            >
              {s.done && <Check size={11} weight="bold" aria-hidden />}
              {s.label}
            </span>
          </li>
        ))}
      </ol>
      <div className="flex items-start gap-2.5 border-t border-rule pt-3">
        <ScoutAvatar />
        <p className="text-sm text-ink">
          Detection read and summarised as an insight, with the evidence attached.
          <span className="mt-0.5 flex items-center gap-1 text-xs text-ink-subtle">
            <Pause size={10} weight="fill" aria-hidden />
            Waiting for your call
          </span>
        </p>
      </div>
    </MockCard>
  );
}
